'use client'

/**
 * Has the user journalled today?
 *
 * A pure selector over the resolved view, plus the thin hook that reads it — ADR 0012's shape for a
 * read. **`useDocumentSelector` does not exist yet**: `document/use-document.ts:7` records that it
 * "travels with the query hooks", which is a later step's work, so the hook calls `useDocumentView`
 * itself and collapses to one line when that lands. The selector is the part that matters, and it
 * is testable without a provider either way.
 *
 * **The directive is load-bearing.** `app/app/journal/page.tsx` is a server component and imports
 * `@/entries`, which pulls every module the face names into the RSC graph — so `useJournalToday` reads the view and
 * this module opens with `'use client'`. Without it `pnpm build` fails collecting `/app/journal`
 * with `createContext is not a function`, and nothing before the build says so (#375).
 */

import { useMemo } from 'react'

import { useDocumentView, type DocumentView } from '@/document'
import { formatDateString } from '@/lib/date'

/** True when the view holds a live journal entry created on `today`. */
export function selectJournalToday(
  view: DocumentView | null,
  today: Date = new Date(),
): boolean {
  if (!view) return false

  const todayPrefix = formatDateString(today)

  return Object.values(view.entries).some(
    (entry) =>
      entry.type === 'journal' &&
      !entry.archivedAt &&
      entry.createdAt.startsWith(todayPrefix),
  )
}

interface JournalTodayResult {
  hasJournaledToday: boolean
}

export function useJournalToday(): JournalTodayResult {
  const view = useDocumentView()
  const hasJournaledToday = useMemo(() => selectJournalToday(view), [view])
  return { hasJournaledToday }
}
